import { useMemo, useState } from 'react'

import {
	createIntakeChannelDraft,
	isIntakeChannelValue,
	type IntakeChannelDraft,
} from '#app/routes/mocra+/_form+/adverse-events/components/intake-channels/types.ts'
import { CHANNEL_CARD_OPTIONS } from '#app/routes/mocra+/_form+/adverse-events/helpers.ts'
import { ADVERSE_EVENTS_SECTION_RESOURCES } from '#app/routes/mocra+/_form+/adverse-events/section-resources.ts'
import { IntakeChannelCard } from '#app/routes/mocra+/_form+/components/intake-channels/card.tsx'
import { getIntakeChannelIcon } from '#app/routes/mocra+/_form+/components/intake-channels/icons.ts'
import { IntakeChannelOverlay } from '#app/routes/mocra+/_form+/components/intake-channels/overlay.tsx'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS } from '#app/routes/mocra+/_form+/intake-options.ts'

type ChannelOptionValue = (typeof CHANNEL_CARD_OPTIONS)[number]['value']

type ChannelListItem = {
	readonly value: ChannelOptionValue
	readonly draft?: IntakeChannelDraft
	readonly errors: readonly string[]
}

type Option = {
	readonly value: string
	readonly label: string
}

type Props = {
	readonly channels: ReadonlyArray<ChannelListItem>
	readonly warnings: ReadonlyArray<string>
	readonly systemOptions: ReadonlyArray<Option>
	readonly onEnsureChannel: (value: ChannelOptionValue) => void
	readonly onSubmitChannel: (
		value: ChannelOptionValue,
		draft: IntakeChannelDraft,
	) => void
	readonly onRemoveChannel: (value: ChannelOptionValue) => void
	readonly disableRemove?: boolean
}

const ownerTypeOptions = COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS.map(
	({ value, label }) => ({
		value,
		label,
	}),
)

const ownerTypeLabelByValue = COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS.reduce<
	Record<string, string>
>((lookup, option) => {
	lookup[option.value] = option.label
	return lookup
}, {})

export function IntakeChannelsSection({
	channels,
	warnings,
	systemOptions,
	onEnsureChannel,
	onSubmitChannel,
	onRemoveChannel,
	disableRemove = false,
}: Props) {
	const [editingState, setEditingState] = useState<{
		channel: ChannelOptionValue
		mode: 'add' | 'edit'
	} | null>(null)

	const channelEntryByValue = useMemo(() => {
		const map = new Map<ChannelOptionValue, ChannelListItem>()
		for (const channel of channels) {
			if (!channel.draft) continue
			map.set(channel.value, channel)
		}
		return map
	}, [channels])

	const systemLabelByValue = useMemo(() => {
		const lookup: Record<string, string> = {}
		for (const option of systemOptions) {
			lookup[option.value] = option.label
		}
		return lookup
	}, [systemOptions])

	const handleSelectChannel = (rawValue: string) => {
		if (!isChannelOptionValue(rawValue)) return
		const existing = channelEntryByValue.get(rawValue)
		if (!existing) {
			onEnsureChannel(rawValue)
			setEditingState({ channel: rawValue, mode: 'add' })
			return
		}
		setEditingState({ channel: rawValue, mode: 'edit' })
	}

	const handleRemoveChannel = (rawValue: string) => {
		if (!isChannelOptionValue(rawValue)) return
		onRemoveChannel(rawValue)
		if (editingState?.channel === rawValue) {
			setEditingState(null)
		}
	}

	const handleOverlaySubmit = (draft: IntakeChannelDraft) => {
		if (!editingState) return
		const value = isChannelOptionValue(editingState.channel)
			? editingState.channel
			: isIntakeChannelValue(draft.channel)
				? draft.channel
				: undefined
		if (!value) {
			setEditingState(null)
			return
		}
		onSubmitChannel(value, draft)
		setEditingState(null)
	}

	const editingEntry = editingState
		? channelEntryByValue.get(editingState.channel)
		: null

	const ownerTypeOptionsMemo = useMemo(() => ownerTypeOptions, [])

	return (
		<Section
			title="Intake channels"
			description="Select every intake channel, then document who operates it and where complaints land after intake."
			mocraSections={ADVERSE_EVENTS_SECTION_RESOURCES.mocraSections}
			templates={ADVERSE_EVENTS_SECTION_RESOURCES.templates}
			meta={
				warnings.length ? (
					<ul className="text-destructive list-disc space-y-1 pl-5 text-xs">
						{warnings.map((warning) => (
							<li key={warning}>{warning}</li>
						))}
					</ul>
				) : null
			}
		>
			<ul className="space-y-3">
				{CHANNEL_CARD_OPTIONS.map((option) => {
					const entry = channelEntryByValue.get(option.value)
					const selected = Boolean(entry?.draft)
					const draft = entry?.draft
					const errors = entry?.errors ?? []
					const ownerTypeLabel = draft
						? (ownerTypeLabelByValue[draft.ownerType] ?? draft.ownerType)
						: null
					const systemLabel = draft
						? (systemLabelByValue[draft.system] ?? draft.system)
						: null

					const ownerNameLabel =
						draft && draft.ownerName.trim().length > 0
							? draft.ownerName
							: 'Owner not documented yet'
					const ownerLine = selected
						? ownerTypeLabel
							? `${ownerNameLabel} • ${ownerTypeLabel}`
							: ownerNameLabel
						: 'Click to add ownership details'
					const systemLine =
						selected && draft
							? systemLabel && systemLabel.trim().length > 0
								? systemLabel
								: 'Logging destination not documented yet'
							: undefined
					const subtitle = [ownerLine, systemLine]
						.filter((line): line is string => Boolean(line && line.length))
						.join(' • ')
					const IconComponent = getIntakeChannelIcon(option.value)
					return (
						<IntakeChannelCard
							key={option.value}
							option={option}
							selected={selected}
							icon={IconComponent}
							subtitle={subtitle}
							subtitleClassName="text-muted-foreground"
							errors={errors}
							onPress={() => handleSelectChannel(option.value)}
							onToggle={(nextSelected) => {
								if (nextSelected) {
									handleSelectChannel(option.value)
									return
								}
								handleRemoveChannel(option.value)
							}}
							disableDeselect={disableRemove}
						/>
					)
				})}
			</ul>
			<IntakeChannelOverlay
				open={Boolean(editingState)}
				mode={editingState?.mode ?? 'edit'}
				channelLabel={
					CHANNEL_CARD_OPTIONS.find(
						(option) => option.value === editingState?.channel,
					)?.label ?? 'Intake channel'
				}
				initialValue={
					editingEntry?.draft ??
					createIntakeChannelDraft(
						editingState?.channel ?? CHANNEL_CARD_OPTIONS[0].value,
					)
				}
				ownerTypeOptions={ownerTypeOptionsMemo}
				systemOptions={systemOptions}
				onSubmit={handleOverlaySubmit}
				onOpenChange={(open) => {
					if (!open) {
						setEditingState(null)
					}
				}}
				onCancel={() => setEditingState(null)}
				onClosed={() => setEditingState(null)}
				onRemove={
					editingState?.mode === 'edit' && editingEntry?.draft && !disableRemove
						? () => handleRemoveChannel(editingState.channel)
						: undefined
				}
			/>
		</Section>
	)
}

function isChannelOptionValue(value: string): value is ChannelOptionValue {
	return isIntakeChannelValue(value)
}
